import { useEffect, useState } from "react";
import { CircularProgress, TextField } from "@mui/material";

import useRelayAction from "../../hooks/useRelayAction";
import { ecoFuturesCoreContractAddress, getReadOnlyContract } from "../../services/contract.service";
import { isUserRejection } from "../../services/relay.service";
import { describeChainError, respondToChallenge } from "../../services/challenge.service";
import { PopupActionButton, PopupGhostButton, PopupProcessingButton } from "../PopupButtons";
import PopupDialog from "../PopupDialog";
import { reasonFieldSx } from "../Settlement/ChallengeDeedDialog";
import {
  CalloutText,
  DetailBody,
  DetailCard,
  DetailTitle,
  DialogSheet,
  ErrorNote,
  Footnote,
  InfoCallout,
  figmaButtonSx,
} from "../Settlement/settlementStyles";

// The challenged verifier's one response (V10.1 §4). One dialog for both stages a verifier can be
// challenged at, because the form is identical and only the contract differs:
//
//   covenant  EcoChallenge.respondToChallenge(challengeId, evidence) — deed or verification stage,
//             against a minted token. DIRECT wallet transaction: EcoChallenge reads raw msg.sender
//             and checks it against c.verifier, so a relayed call would arrive as the forwarder and
//             revert NotTheChallengedVerifier. The verifier pays gas.
//   premint   EcoFuturesCore.respondToChallenge(requestId, evidence) — before anything is minted.
//             Core is ERC-2771, so this one goes through the relay: one signature, no gas.
//
// One response, no revision (AlreadyResponded). Silence is allowed and does not decide the outcome
// — the panel is drawn once the response window lapses either way (§6) — so this exists to make
// answering possible, not mandatory, and the copy says so.

export type RespondStage = "covenant" | "premint";

const STATUS_COPY: Record<string, string> = {
  signing: "Waiting for your wallet signature…",
  relaying: "Submitting to the relay…",
  confirming: "Waiting for on-chain confirmation…",
};

export interface RespondToChallengeDialogProps {
  open: boolean;
  onClose: () => void;
  stage: RespondStage;
  // EcoChallenge challengeId (covenant) or the on-chain verification requestId (premint).
  id: bigint | number | string;
  // The challenger's evidence, so the verifier answers what was actually alleged.
  challengeEvidence?: string | null;
  challengedBy?: string | null;
  landName?: string | null;
  // Called once the response is mined, so the caller can re-read.
  onResponded?: () => void;
}

const shortAddress = (addr: string) => `${addr.slice(0, 6)}…${addr.slice(-4)}`;

const RespondToChallengeDialog = ({
  open,
  onClose,
  stage,
  id,
  challengeEvidence,
  challengedBy,
  landName,
  onResponded,
}: RespondToChallengeDialogProps) => {
  const [evidence, setEvidence] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const relay = useRelayAction();

  useEffect(() => {
    if (open) {
      setEvidence("");
      setError(null);
      relay.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const pending = busy || relay.isPending;

  const handleClose = () => {
    if (pending) {
      return;
    }
    onClose();
  };

  const submit = async () => {
    const text = evidence.trim();
    if (!text) {
      return;
    }
    setError(null);
    if (stage === "covenant") {
      setBusy(true);
      try {
        await respondToChallenge(BigInt(id), text);
        onResponded?.();
        onClose();
      } catch (e: any) {
        if (!isUserRejection(e)) {
          setError(describeChainError(e));
        }
      } finally {
        setBusy(false);
      }
      return;
    }
    const core = getReadOnlyContract("ecofutures-core");
    if (!core) {
      setError("Core contract not found");
      return;
    }
    const result = await relay.run({
      to: ecoFuturesCoreContractAddress,
      iface: core.interface,
      fn: "respondToChallenge",
      args: [id, text],
      confirmation: {
        address: ecoFuturesCoreContractAddress,
        iface: core.interface,
        eventName: "ChallengeResponded",
      },
      label: "Respond to challenge",
    });
    if (result) {
      onResponded?.();
      onClose();
    }
  };

  const shownError = error ?? relay.error;

  return (
    <PopupDialog open={open} onClose={handleClose} surface="sheet">
      <DialogSheet>
        <InfoCallout surface="white">
          <CalloutText>
            Your verification of {landName ? <strong>{landName}</strong> : "this land"} has been challenged. You may
            file one response, which is recorded on-chain beside the challenger&apos;s evidence and read by the panel.
            Silence is allowed — it does not count against you — but this is your one chance to answer.
          </CalloutText>
        </InfoCallout>

        <DetailCard>
          <DetailTitle>The challenge</DetailTitle>
          <DetailBody>{challengeEvidence || "No evidence text could be loaded for this challenge."}</DetailBody>
          {challengedBy && (
            <DetailBody style={{ marginTop: "8px", color: "#888" }}>Raised by {shortAddress(challengedBy)}</DetailBody>
          )}
        </DetailCard>

        <TextField
          fullWidth
          multiline
          minRows={3}
          placeholder="What supports your verification, and why does the challenge not hold?"
          value={evidence}
          onChange={(e) => setEvidence(e.target.value)}
          disabled={pending}
          sx={reasonFieldSx}
        />

        {shownError && <ErrorNote>{shownError}</ErrorNote>}

        {pending ? (
          <PopupProcessingButton disabled variant="contained" fullWidth sx={figmaButtonSx}>
            <CircularProgress size={20} sx={{ color: "white", mr: "10px" }} />
            {stage === "covenant" ? "Confirm the transaction in your wallet…" : STATUS_COPY[relay.status] ?? "Working…"}
          </PopupProcessingButton>
        ) : (
          <>
            <PopupActionButton
              onClick={submit}
              disabled={!evidence.trim()}
              variant="contained"
              fullWidth
              sx={figmaButtonSx}
            >
              {stage === "covenant" ? "File response — pays gas" : "File response — no gas needed"}
            </PopupActionButton>
            <PopupGhostButton onClick={handleClose} fullWidth>
              Not now
            </PopupGhostButton>
          </>
        )}

        <Footnote>
          One response, no revision. Only the challenged verifier&apos;s wallet can file it.
          {stage === "covenant"
            ? " Sent as a wallet transaction — you pay the gas."
            : " Approved with a wallet signature through the relay."}
        </Footnote>
      </DialogSheet>
    </PopupDialog>
  );
};

export default RespondToChallengeDialog;
